import { useEffect, useState } from 'react';
import usePageMeta from '../utils/usePageMeta';
import pagesSeo from '../data/pagesSeo.json';
import Header from '../components/core/Header';
import Footer from '../components/core/Footer';
import styles from './PeoplePage.module.css';
import typography from '../theme/typography';
import colors from '../theme/colors';
import { isContentfulConfigured } from '../contentful/client';
import { fetchMembersGroupedBySection } from '../contentful/membersApi';

import photoKalyan from '../assets/images/people/kalyan-veeramachaneni.jpg';
import photoKatie from '../assets/images/people/katie-oreilly.jpg';
import photoLaure from '../assets/images/people/laure-berti-equille.jpg';
import photoAlfredo from '../assets/images/people/alfredo-cuesta-infante.png';
import photoMax from '../assets/images/people/max-kanter.jpg';
import photoAlexandra from '../assets/images/people/alexandra-zytek.jpg';
import photoSarah from '../assets/images/people/sarah-alnegheimish.jpg';
import photoSara from '../assets/images/people/sara-pido.jpg';
import photoMichaelF from '../assets/images/people/michael-fuest.jpg';
import photoWarren from '../assets/images/people/warren-wang.jpeg';
import photoRaymond from '../assets/images/people/raymond-pan.jpg';
import photoLinh from '../assets/images/people/linh-nguyen.jpeg';
import photoAngela from '../assets/images/people/angela-zhao.jpg';
import photoSalim from '../assets/images/people/salim-cherkaoui.jpeg';
import photoSantu from '../assets/images/people/shubhra-kanti-karmaker.jpg';
import photoIvan from '../assets/images/people/ivan-ramirez-diaz.jpg';
import photoAlicia from '../assets/images/people/alicia-yi-sun.jpg';
import photoLei from '../assets/images/people/lei-xu.jpg';
import photoMicah from '../assets/images/people/micah-j-smith.jpg';
import photoDongyu from '../assets/images/people/dongyu-liu.jpeg';

const principal = {
  name: 'Kalyan Veeramachaneni',
  email: 'kalyan@csail.mit.edu',
  photo: photoKalyan,
};

const groupAdmin = {
  name: "Katie O'Reilly",
  email: 'oreilly1@mit.edu',
  photo: photoKatie,
};

const FALLBACK_VISITING = [
  { id: 'local-laure', name: 'Laure Berti-équille', type: 'Research Affiliate', photo: photoLaure },
  { id: 'local-alfredo', name: 'Alfredo Cuesta-Infante', type: 'Research Affiliate', photo: photoAlfredo },
  { id: 'local-max', name: 'Max Kanter', type: 'Visiting Scholar', photo: photoMax },
];

const FALLBACK_MEMBERS = [
  { id: 'local-alex', name: 'Alexandra Zytek', type: 'PhD', photo: photoAlexandra },
  { id: 'local-sarah', name: 'Sarah Alnegheimish', type: 'PhD', photo: photoSarah },
  { id: 'local-sara', name: 'Sara Pidò', type: 'Postdoc', photo: photoSara },
  { id: 'local-mf', name: 'Michael Fuest', type: 'Visiting Student', photo: photoMichaelF },
  { id: 'local-ww', name: 'Warren Wang', type: 'PhD', photo: photoWarren },
  { id: 'local-rp', name: 'Raymond Pan', type: 'MEng', photo: photoRaymond },
  { id: 'local-ln', name: 'Linh Nguyen', type: 'MEng', photo: photoLinh },
  { id: 'local-az', name: 'Angela Zhao', type: 'MEng', photo: photoAngela },
  { id: 'local-sc', name: 'Salim Cherkaoui', type: 'Visiting Student', photo: photoSalim },
];

const FALLBACK_ALUMNI = [
  { id: 'local-santu', name: 'Shubhra Kanti Karmaker ("Santu")', type: 'Postdoc', photo: photoSantu },
  { id: 'local-ivan', name: 'Ivan Ramirez Diaz', type: 'Postdoc', photo: photoIvan },
  { id: 'local-alicia', name: 'Alicia Yi Sun', type: 'PhD', photo: photoAlicia },
  { id: 'local-lei', name: 'Lei Xu', type: 'PhD', photo: photoLei },
  { id: 'local-micah', name: 'Micah J. Smith', type: 'PhD', photo: photoMicah },
  { id: 'local-dl', name: 'Dongyu Liu', type: 'Postdoc', photo: photoDongyu },
];

const MORE_ALUMNI_COL1 = [
  'Grace Song (Masters 2024)',
  'Guanpeng Xu (Masters 2024)',
  'Nassim Oufattole (Masters 2022, PhD Student MIT)',
  'Frances Hartwell (Masters 2022, DataCebo)',
  'Serene Hamsho (Visiting Scientist 2022, Hexicon)',
  'Hugo Ramirez (UROP 2022)',
  'Lawrence Wong (Masters 2022)',
  'Romain Palazzo (Visiting Student 2022, DataCebo)',
  'Michaela Henry (Program Coordinator 2021, Brandeis)',
  'Plamen Valentinov Kolev (Software Engineer 2021, DataCebo)',
  'Bryan Farris (Software Engineer 2020, Goldfish Code)',
  'Carles Salas (Software Engineer 2020)',
  'Seth Amarasinghe (Visiting Student 2022)',
  'Kevin Zhang (Masters 2020, Meta)',
  'Ajinkya Nene (Masters 2020, Trellus)',
  'Felipe Hoffman (Masters 2020, DataCebo)',
  'Harry Yu (UROP)',
  'Katherine Wang (Masters 2020, Stripe)',
  'Van Coykendall (UROP 2019, MIT)',
  'Alex Geiger (Visiting Masters 2019)',
  'Kelvin Lu (Masters 2019, Amplitude)',
  'Ihssan Tinawi (Masters 2019, Nash)',
  'Zara Perumal (Masters 2018, Google)',
  'Caroline Morganti (Masters 2018, IBM)',
  'Bingfei (Tony) Cao (Masters 2018, Benchling)',
];

const MORE_ALUMNI_COL2 = [
  'Alexander Nordin (Masters 2018, Butterfly Network)',
  'Laura Gustafson (Masters 2018, Meta)',
  'Akshay Ravikumar (Masters 2018, Schoolhouse.world)',
  'William Xue (Masters 2018, Google)',
  'Andrew Montanez (Masters 2017, DataCebo)',
  'Katherine Xiao (Masters 2017, DataCebo)',
  'Michael Choi (Masters 2017, Scale AI)',
  'Jonathan Johannemann (Masters 2017, Parafin)',
  'Bennett Cyphers (Masters 2017, UC Berkeley Law)',
  'David Wong (Masters 2017, Vanta)',
  'Thomas Swearingen (Visiting 2017, MSU)',
  'Alec Anderson (Masters 2017, D.E.Shaw Group)',
  'Benjamin Schreck (Masters 2016, Coinbase)',
  'Neha Patki (Masters 2016, DataCebo)',
  'Jason Wu (Masters 2016, Affirm)',
  'Sebastien Boyer (Masters 2016, Farmwise)',
  'Mario Orozco (Masters 2016)',
  'Max Kanter (Masters 2015, Feature Labs)',
  'Bryan Collazo (Masters 2015, Connect Assistance)',
  'Sebastien Dubois (Visiting Masters 2015, Airbnb)',
  'Alex Wang (Masters 2015)',
  'Edwin Zhang (Masters 2015, DoorDash)',
  'Michael Wu (Masters 2015)',
];

const FALLBACK_COLLABORATORS = [
  'Dr. Ignacio Arnaldo (PatternEx)',
  'Prof. Carlotta Domeniconi (GMU)',
  'Prof. Ken Koedinger (CMU)',
  'Prof. Arun Ross (MSU)',
];

const PhotoPlaceholder = ({ size = 200 }) => (
  <div
    className={styles.photoPlaceholder}
    style={{ width: size, height: size, borderRadius: '50%', background: '#d4d4cc' }}
  />
);

const PersonCard = ({ person }) => {
  const typeLine = person.type || person.role;
  return (
    <div className={styles.personCard}>
      {person.photo ? (
        <img src={person.photo} alt={person.name} className={styles.photoLarge} />
      ) : (
        <PhotoPlaceholder size={200} />
      )}
      <p className={styles.personName}>{person.name}</p>
      {typeLine && <p className={styles.personRole}>{typeLine}</p>}
    </div>
  );
};

/**
 * Splits a flat "More alumni" list from Contentful, or fall back to static two-column data.
 * @param {{ id: string, name: string }[]} rows
 */
function splitMoreAlumniRows(rows) {
  if (!rows || rows.length === 0) {
    return { col1: MORE_ALUMNI_COL1, col2: MORE_ALUMNI_COL2, fromCms: false };
  }
  const mid = Math.ceil(rows.length / 2);
  return {
    col1: rows.slice(0, mid).map((r) => r.name),
    col2: rows.slice(mid).map((r) => r.name),
    fromCms: true,
  };
}

const PeoplePage = () => {
  usePageMeta(pagesSeo.people);
  const [grouped, setGrouped] = useState(null);
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    if (!isContentfulConfigured()) {
      return;
    }
    let cancel = false;
    (async () => {
      try {
        const data = await fetchMembersGroupedBySection();
        if (cancel) {
          return;
        }
        if (data) {
          setGrouped(data);
        }
      } catch (err) {
        console.error('Failed to load members from Contentful', err);
        if (!cancel) {
          setLoadFailed(true);
        }
      }
    })();
    return () => {
      cancel = true;
    };
  }, []);

  const hasCms = Boolean(grouped) && !loadFailed;

  /**
   * When Contentful is active, use only that data per section (no static fallback for empty).
   * When it is not, use the built-in static lists.
   */
  const visitingScholars = hasCms
    ? (grouped['Visiting Scholars/Affiliates'] || [])
    : FALLBACK_VISITING;
  const showVisiting = visitingScholars.length > 0;

  const adminList = hasCms && grouped.Admin?.length ? grouped.Admin : null;
  const projectManagers = hasCms && grouped['Project Manager']?.length ? grouped['Project Manager'] : null;

  const membersList = hasCms ? (grouped.Members || []) : FALLBACK_MEMBERS;
  const showMembers = membersList.length > 0;

  const alumniList = hasCms ? (grouped.Alumni || []) : FALLBACK_ALUMNI;
  const showAlumni = alumniList.length > 0;

  const moreAlumniCms = hasCms ? (grouped['More Alumni'] || []) : null;
  const showMoreAlumniBlock = !hasCms || (moreAlumniCms && moreAlumniCms.length > 0);
  const moreAlumniSplit = showMoreAlumniBlock
    ? splitMoreAlumniRows(moreAlumniCms === null ? [] : moreAlumniCms)
    : { col1: [], col2: [], fromCms: false };
  const { col1: moreAlumni1, col2: moreAlumni2, fromCms: moreAlumniListFromCms } = moreAlumniSplit;

  const collaborators = hasCms
    ? (grouped.Collaborators || []).map((c) => c.name)
    : FALLBACK_COLLABORATORS;
  const showCollaborators = collaborators.length > 0;
  const showMoreSection = showMoreAlumniBlock || showCollaborators;

  return (
    <div className={styles.page}>
      <Header />

      <div className={styles.container}>
        <h1
          className={`${styles.pageTitle} page-primary-title`}
          style={{ ...typography.heading.large, color: colors.dark }}
        >
          PEOPLE
        </h1>
        <hr className={styles.divider} />

        <div className={styles.topSection}>
          <div className={styles.topGroup}>
            <h2 className={styles.groupLabel}>Principal Investigator</h2>
            <div className={styles.topCard}>
              {principal.photo ? (
                <img src={principal.photo} alt={principal.name} className={styles.photoLarge} />
              ) : (
                <PhotoPlaceholder size={200} />
              )}
              <p className={styles.personName}>{principal.name}</p>
              <a href={`mailto:${principal.email}`} className={`${styles.personAffiliation} ${styles.link}`}>
                {principal.email}
              </a>
            </div>
          </div>

          <div className={styles.topGroup}>
            <h2 className={styles.groupLabel}>Group Admin</h2>
            <div className={styles.topCard}>
              {groupAdmin.photo ? (
                <img src={groupAdmin.photo} alt={groupAdmin.name} className={styles.photoLarge} />
              ) : (
                <PhotoPlaceholder size={200} />
              )}
              <p className={styles.personName}>{groupAdmin.name}</p>
              <a href={`mailto:${groupAdmin.email}`} className={`${styles.personAffiliation} ${styles.link}`}>
                {groupAdmin.email}
              </a>
            </div>
          </div>
        </div>

        {adminList && (
          <div className={styles.block}>
            <h2 className={styles.sectionHeading}>Admin</h2>
            <div className={styles.grid4}>
              {adminList.map((p) => (
                <PersonCard key={p.id} person={p} />
              ))}
            </div>
          </div>
        )}

        {projectManagers && (
          <div className={styles.block}>
            <h2 className={styles.sectionHeading}>Project Manager</h2>
            <div className={styles.grid4}>
              {projectManagers.map((p) => (
                <PersonCard key={p.id} person={p} />
              ))}
            </div>
          </div>
        )}

        {showVisiting && (
          <div className={styles.block}>
            <h2 className={styles.sectionHeading}>Visiting Scholars/Affiliates</h2>
            <div className={styles.grid3}>
              {visitingScholars.map((p) => (
                <PersonCard key={p.id || p.name} person={p} />
              ))}
            </div>
          </div>
        )}

        {showMembers && (
          <div className={styles.block}>
            <h2 className={styles.sectionHeading}>Members</h2>
            <div className={styles.grid4}>
              {membersList.map((p) => (
                <PersonCard key={p.id || p.name} person={p} />
              ))}
            </div>
          </div>
        )}

        {showAlumni && (
          <div className={styles.block}>
            <h2 className={styles.sectionHeading}>Alumni</h2>
            <div className={styles.grid4}>
              {alumniList.map((p) => (
                <PersonCard key={p.id || p.name} person={p} />
              ))}
            </div>
          </div>
        )}

        {showMoreSection && (
          <div className={styles.moreSection}>
            {showMoreAlumniBlock && (
              <div className={styles.moreAlumni}>
                <h2 className={styles.sectionHeading}>More Alumni</h2>
                <div className={styles.alumniColumns}>
                  <ul className={styles.alumniList}>
                    {moreAlumniListFromCms
                      ? moreAlumni1.map((line, i) => <li key={`a-${i}`}>{line}</li>)
                      : MORE_ALUMNI_COL1.map((name) => <li key={name}>{name}</li>)}
                  </ul>
                  <ul className={styles.alumniList}>
                    {moreAlumniListFromCms
                      ? moreAlumni2.map((line, i) => <li key={`b-${i}`}>{line}</li>)
                      : MORE_ALUMNI_COL2.map((name) => <li key={name}>{name}</li>)}
                  </ul>
                </div>
              </div>
            )}

            {showCollaborators && (
              <div className={styles.collaborators}>
                <h2 className={styles.sectionHeading}>Collaborators</h2>
                <ul className={styles.collaboratorList}>
                  {collaborators.map((c) => (
                    <li key={c}>{c}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
};

export default PeoplePage;
